import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, Subject, takeUntil } from 'rxjs';
import { Bl, BlDetails, BlMaster, BlMasterCreate, BlMasterResponse, CabotageBoat, ClientesBl, ExportBoats, FileBl, FileBlSaved, GetFileBl, OperationType, ShipBalance, ShipBalanceResponse } from 'src/app/interfaces/bl.interfaces';
import { GLOBAL } from 'src/app/_config/config';
import { Router } from '@angular/router';

/**
 * Servicio que gestiona los Bill of Lading (BL) y barcos del sistema,
 * incluyendo la consulta de barcos por año y módulo, los BL maestros y de
 * detalle, sus archivos, y la lógica de filtrado y ordenamiento de las tablas
 * de barcos por tipo de operación y estado.
 */
@Injectable({
  providedIn: 'root'
})
export class BlService {
  private cancelPendingRequests$ = new Subject<void>();
  currentShipName: string = '';
  currentOperationId: number = 0;
  shipType: string = 'Todos';
  overlayError: boolean = false;
  ships: boolean = false;
  blDetails: BlDetails[] = [];
  path: string = '';
  loader: boolean = false;
  textFilter: string = '';
  idCliente: number = 0;
  bls: Bl[] = [];
  blackListListaBl: string[] = ['id_operation', 'abandonment_status', 'days_abandonment'];
  blackListListaImport: string[] = ['operation_type', 'id_operation', 'abandonment_status', 'days_abandonment'];
  private _bls: Bl[] = [];
  typeOperation: OperationType = OperationType.General;
  loaderTable: boolean = false;
  cabotageBoats: number[] = [];

  get blsFilter() {
    return this._bls;
  }

  set blsFilter(value: Bl[]) {
    this._bls = value;
  }

  private _currentBlId: number = 0;

  get currentBlId() {
    return this._currentBlId;
  }

  set currentBlId(value: number) {
    this._currentBlId = value;
  }

  constructor( private http: HttpClient, private router: Router, ) { }

  /**
   * Obtiene la lista completa de BL/barcos.
   * @returns Observable con la lista de barcos.
   */
  getBl(): Observable<Bl[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.list}`;
    return this.http.get<Bl[]>(url);
  }

  /**
   * Obtiene los barcos de un año, redondeando el tonelaje restante a 3 decimales.
   * @param year - Año a consultar.
   * @returns Observable con la lista de barcos del año.
   */
  getShipListByYear(year: number): Observable<Bl[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.listByYear}${year}`;
    return this.http.get<Bl[]>(url).pipe(
      map(ships => {
        return ships.map( ship => ({...ship, remaining_tonnage: Number(ship.remaining_tonnage.toFixed(3))}))
      })
    );
  }

  /**
   * Obtiene los BL maestros de una operación.
   * @param operation_number - Número de operación.
   * @returns Observable con la lista de BL maestros.
   */
  getBlMaster(operation_number: number): Observable<BlMaster[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.bl.bl_master}${operation_number}`;
    return this.http.get<BlMaster[]>(url);
  }

  /**
   * Obtiene el detalle de los BL de una operación.
   * @param operation_number - Número de operación.
   * @returns Observable con el detalle de los BL.
   */
  getBlId(operation_number: number): Observable<BlDetails[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.get}${operation_number}`;
    return this.http.get<BlDetails[]>(url);
  }

  /**
   * Obtiene el detalle de un BL a partir de su identificador y BL maestro.
   * @param bl_id - Identificador del BL.
   * @param bl_master - Identificador del BL maestro.
   * @returns Observable con el detalle del BL.
   */
  getBlIdMaster(bl_id: number, bl_master: string): Observable<BlDetails[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.bl.bl_details}${bl_id}/${bl_master}`;
    return this.http.get<BlDetails[]>(url);
  }

  /**
   * Crea un BL maestro.
   * @param bl_master - Datos del BL maestro a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createBlMaster(bl_master: BlMasterCreate): Observable<BlMasterResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.bl.bl_masterCreate}`;
    return this.http.post<BlMasterResponse>(url, bl_master);
  }

  /**
   * Actualiza un BL maestro existente.
   * @param bl_master - Datos del BL maestro a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateBlMaster(bl_master: BlMasterCreate): Observable<{}> {
    const url: string = `${GLOBAL.url}${GLOBAL.bl.bl_masterUpdate}`;
    return this.http.put(url, bl_master);
  }

  /**
   * Obtiene los clientes asociados a un BL de una operación.
   * @param operation_number - Número de operación.
   * @returns Observable con la lista de clientes del BL.
   */
  getClientesBl(operation_number: number): Observable<ClientesBl[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.bl.bl_customer}${operation_number}`;
    return this.http.get<ClientesBl[]>(url);
  }

  /**
   * Crea el archivo asociado a un BL.
   * @param infoFile - Datos del archivo del BL a crear.
   * @returns Observable con el archivo guardado.
   */
  createFileBl(infoFile: FileBl): Observable<FileBlSaved> {
    const url: string = `${GLOBAL.url}${GLOBAL.bl.file.create}`;
    return this.http.post<FileBlSaved>(url, infoFile);
  }

  /**
   * Obtiene el archivo asociado a un BL.
   * @param bl_id - Datos para identificar el archivo del BL.
   * @returns Observable con el archivo del BL.
   */
  getFileBl(bl_id: GetFileBl): Observable<FileBlSaved> {
    const url: string = `${GLOBAL.url}${GLOBAL.bl.file.get}`;
    return this.http.post<FileBlSaved>(url, bl_id);
  }

  /**
   * Obtiene los barcos asociados a un módulo.
   * @param module - Identificador del módulo.
   * @returns Observable con la lista de barcos del módulo.
   */
  getBoats(module: number): Observable<ExportBoats[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.listByModule}${module}`;
    return this.http.get<ExportBoats[]>(url);
  }

  /**
   * Obtiene la lista de barcos de cabotaje.
   * @returns Observable con la lista de barcos de cabotaje.
   */
  getCabotageBoats(): Observable<CabotageBoat[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.cabotageList}`;
    return this.http.get<CabotageBoat[]>(url);
  }

  /**
   * Actualiza el balance de tonelaje de un barco.
   * @param body - Datos del balance del barco a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateShipBalance(body: ShipBalance): Observable<ShipBalanceResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.barco.balance}`;
    return this.http.post<ShipBalanceResponse>(url, body);
  }

  /**
   * Carga y prepara la tabla de barcos de un año, aplicando ordenamiento,
   * formato de fechas y filtros, y gestionando los estados de carga y error.
   * @param year - Año a consultar; usa el año actual si no se especifica.
   */
  getBlTable(year?: number) {
    this.bls = [];
    this.blsFilter = [];
    this.loader = true;
    const currenYear: number = year ? year : new Date().getFullYear();
    this.getShipListByYear(currenYear)
    .pipe(takeUntil(this.cancelPendingRequests$))
    .subscribe({
      next: bls => {
        if (!Array.isArray(bls)) {
          this.ships = true;
          return;
        }
        this.ships = false;
        bls = this.sortArray(bls, 'operation_number');
        bls = this.fieldDate(bls);
        this.bls = [...bls];
        this.blsFilter = [...bls];
        this.sortByFilter();
        this.loader = false;
      },
      error: (e) => {
        this.loader = false;
        this.overlayError = true;
      },
    });
  }

  /**
   * Cancela las peticiones HTTP pendientes emitiendo en el subject de cancelación.
   */
  cancelRequests() {
    this.cancelPendingRequests$.next();
  }

  /**
   * Filtra los barcos según el tipo de operación seleccionado y su estado
   * (activos, inactivos o todos), actualizando la lista filtrada.
   */
  sortByTypeOperationAndActive() {
    const isShipsActive: boolean = this.shipType === 'Activos' ? true : false;
    if (this.typeOperation === OperationType.General) {
      this.blsFilter = [...this.bls.filter(item => {
        if (this.shipType === 'Todos') {
          return true;
        } else {
          return item.active === isShipsActive;
        }
      })];
    } else if (this.typeOperation === OperationType.Importation) {
      this.blsFilter = this.bls.filter(item => {
        let match = false;
        if ((item.operation_type_number == this.typeOperation)) {
          if (this.shipType === 'Todos')
            match = true;
          else
            match = item.active === isShipsActive;
        }
        return match;
      });
    } else if (this.typeOperation === OperationType.Exportation) {
      this.blsFilter = this.bls.filter(item => {
        let match = false;
        if ((item.operation_type_number == this.typeOperation)) {
          if (this.shipType === 'Todos')
            match = true;
          else
            match = item.active === isShipsActive;
        }
        return match;
      });
    } else if (this.typeOperation === OperationType.CabotageImp) {
      this.blsFilter = this.bls.filter(item => {
        let match = false;
        if ((item.operation_type_number === OperationType.CabotageImp)) {
          if (this.shipType === 'Todos')
            match = true;
          else
            match = item.active === isShipsActive;
        }
        return match;
      });
    } else if (this.typeOperation === OperationType.CabotageExp) {
      this.blsFilter = this.bls.filter(item => {
        let match = false;
        if ((item.operation_type_number === OperationType.CabotageExp)) {
          if (this.shipType === 'Todos')
            match = true;
          else
            match = item.active === isShipsActive;
        }
        return match;
      });
    }
  }

  /**
   * Aplica el filtro de texto y el ordenamiento sobre la lista de barcos,
   * gestionando opcionalmente el indicador de carga.
   * @param withLoading - Si es `true`, muestra el indicador de carga durante el proceso.
   */
  sortByFilter(withLoading: boolean = true) {
    if (withLoading) {
      this.loader = true;
    }
    this.sortByTypeOperationAndActive();
    if (this.textFilter === '') {
      this.sortArray(this.blsFilter, 'operation_number');
      if (withLoading) {
        setTimeout(() => {
          this.loader = false;
        }, 100);
      }
      return;
    } else {
      this.blsFilter = this.blsFilter.filter(item => {
        const values = Object.entries(item);
        let match = false;
        values.forEach( value => {
          if (this.router.url === '/ship-list') {
            if (!this.blackListListaBl.includes(value[0])) {
              if ((value[1] !== null) && value[1].toString().toLowerCase().includes(this.textFilter.toLowerCase())) {
                match = true;
              }
            }
          } else {
            if (!this.blackListListaImport.includes(value[0])) {
              if ((value[1] !== null) && value[1].toString().toLowerCase().includes(this.textFilter.toLowerCase())) {
                match = true;
              }
            }
          }
        });
        return match;
      });
      this.sortArray(this.blsFilter, 'operation_number');
    }
    if (withLoading) {
      setTimeout(() => {
        this.loader = false;
      }, 1);
    }
  }

  /**
   * Ordena un arreglo de objetos de forma descendente según la clave indicada.
   * @param array - Arreglo a ordenar.
   * @param key - Clave del objeto por la que se ordena.
   * @returns El arreglo ordenado.
   */
  sortArray<T>(array: T[], key: keyof T): T[] {
    return array.sort(function (a, b) {
      if (b[key] < a[key]) {
        return -1;
      }
      if (b[key] > a[key]) {
        return 1;
      }
      return 0;
    });
  }

  /**
   * Recorta los campos de fecha de inicio y fin de operaciones al formato
   * de fecha y hora (16 caracteres).
   * @param array - Lista de barcos a formatear.
   * @returns La lista con las fechas recortadas.
   */
  fieldDate(array: Bl[]): Bl[] {
    array.forEach(element => {
      element.start_operations = element.start_operations !== null ? element.start_operations.substring(0, 16) : element.start_operations;
      element.end_operations = element.end_operations !== null ? element.end_operations.substring(0, 16) : element.end_operations;
    });
    return array;
  }
}
